<template>
    <div>
        <template v-if="loggingIn">
            <loading :message="$t('JOIN_LOGGING_IN')"/>
        </template>
        <template v-else-if="creatingUser">
            <loading :message="$t('JOIN_CREATING_ACCOUNT')"/>
        </template>
        <v-alert v-else-if="error && !retryableError" role="alert" type="error" icon="error" dense :value="error" class="mb-4">{{ error }}</v-alert>
        <h1 v-html="$t('JOIN_TITLE', {competitionName: competition.name})"/>
        <template v-if="competition.id">
            <v-alert v-if="error && retryableError" role="alert" type="error" icon="error" dense :value="error" class="mb-4">{{ error }}</v-alert>
            <announcement/>
            <v-spacer></v-spacer>
            <login-buttons @confirm="onLoginSuccess" mode="join" :providers="providers" :competitionId="competitionId" :credential-code="error ? undefined : credentialCode"/>
        </template>
    </div>
</template>


<script lang="ts">
import Vue from "vue";
import { setTimeout } from "timers";
import Loading from "@utils/Loading.vue";
import LoginButtons from "../login/LoginButtons.vue";
import Announcement from "@utils/Announcement.vue";
import Component from "vue-class-component";
import { useAuthorizationStore } from "@/stores/authorizationStore";
import { ICompetition, CompetitionFilter } from '@cyber-range/cyber-range-api-ctf-competition-client';
import Config from '@/config';
import { Base64 } from "js-base64";
import { useErrorStore } from "@/stores/errorStore";
import { useApiClientStore } from "@/stores/apiClientStore";
import { useAuthenticationStore } from "@/stores/authenticationStore";
import { useEntitlementStore } from "@/stores/entitlementStore";
import { storeToRefs } from "pinia";

@Component({
    components: { Loading, LoginButtons, Announcement }
})
export default class Join extends Vue 
{
    get competitionApiClient() { return useApiClientStore().competitionApiClient; }
    get userApiClient() { return useApiClientStore().userApiClient; }
    getSsoToken = () => useAuthenticationStore().getSsoToken();
    tryLogin = () => useAuthenticationStore().tryLogin();
    loadingBegin = () => useApiClientStore().loadingBegin();
    loadingEnd = () => useApiClientStore().loadingEnd();
    
    fetchClaims = () => useEntitlementStore().fetchClaims();
    canAccessCompetition = useAuthorizationStore().canAccessCompetition;
    canRegisterAsAdmin = useAuthorizationStore().canRegisterAsAdmin;
    canRegisterAsPlayer = useAuthorizationStore().canRegisterAsPlayer;

    // TODO: Change to composition api
    error = storeToRefs(useErrorStore()).errorMessage;
    // END TODO

    competition:ICompetition = <ICompetition> {};
    creatingUser: boolean = false;
    retryableError: boolean = true;
    loggingIn:boolean = false;

    get credentialCode() {
        return this.$route.query ? this.$route.query.code : undefined;
    }

    get competitionId(): string|undefined
    {
        if(this.$route.params.ctfid)
        {
            return this.$route.params.ctfid
        }

        let state = JSON.parse(Base64.decode(<string>this.$route.query?.state)) || {};

        return state.competitionId;
    }

    get providers()
    {
        const providers = ['google', 'azure']
        if (this.competition.settings?.allowDirectLogin)
        {
            providers.push('credential_user')
        }
        return providers;
    }

    async created() 
    {
        let page = await this.competitionApiClient.get(new CompetitionFilter(<any>{subdomain:this.competitionId}));
        
        if(page.items.length === 0)
        {
            useErrorStore().setError({message: this.$root.$t('JOIN_COMPETITION_NOT_FOUND')});
            this.retryableError = false;
            return;
        }

        this.competition = page.items[0];
    }

    async onLoginSuccess() 
    {
        let redirectUrlFromState = this.$route.query?.state ? JSON.parse(Base64.decode(<string>this.$route.query?.state))?.redirectUrl : undefined;

        let ctfRedirectUrl = redirectUrlFromState || (<string> (this.$route.query.redirect_url 
                             ?  
                             this.$route.query.redirect_url :
                             (this.$route.query.state 
                                ? Config.CTF_UI_BASE_URL.replace('www', this.competitionId)
                                : Config.CTF_UI_BASE_URL)));

        await this.tryLogin();
        await this.fetchClaims();

        // Disable all buttons while the user is being redirected to the destination.
        this.loadingBegin();

        if(this.canAccessCompetition(this.competition.id, this.competition.organizationId))
        {
            this.loggingIn = true;
            await this.redirectToUrl(ctfRedirectUrl);
        }
        else if(this.credentialCode || this.canRegisterAsPlayer(this.competition.courseId) || this.canRegisterAsAdmin(this.competition.courseId))
        {
            await this.createUser(this.competition.subdomain);
            await this.redirectToUrl(ctfRedirectUrl);
        }
        else if(this.competition.settings?.endTime && this.competition.settings?.endTime <= new Date().toISOString())
        {
            useErrorStore().setError({message: this.$t("JOIN_FAILED_COMPETITION_ENDED")});
        }
        else if(this.competition.settings?.allowPublicRegistration)
        {
            await this.createUser(this.competition.subdomain);
            await this.redirectToUrl(ctfRedirectUrl);
        }
        else
        {
            useErrorStore().setError({message: this.$t("JOIN_FAILED_COMPETITION_PRIVATE")});

            this.loadingEnd();
        }
    }

    async createUser(subdomain:string)
    {
        try  
        {
            this.creatingUser = true;

            await this.userApiClient.createUserByCtfCompetitionSubdomain(subdomain);

            await new Promise((resolve, reject) =>setTimeout(resolve, 2000));

            await this.tryLogin();
        } 
        catch(e) 
        {
            //Do not set `creatingUser` back to true if all goes well. 
            //This is so that the loading kept showing until page redirection below.
            this.creatingUser = false; 
            throw e;
        }
    }

    async redirectToUrl(url:string): Promise<void>
    {
        try
        {
            let ssoToken = await this.getSsoToken();

            if(!Config.REDIRECT_URLS.split(',').some(u => u === url || new RegExp(u).test(url) === true))
            {
                return useErrorStore().setError({ message: this.$t("INVALID_REDIRECT_URL", {url: this.$route.query.redirect_url}) });
            }

            if(!url.includes('?'))
            {
                url += '?';
            }    

            window.location.href = `${url}&s=${ssoToken}`;
        }
        catch(e)
        {
            this.loggingIn = false;
        }
    }
}
</script>
